import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/SEO";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" /></div>;
  }
  if (!post) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-semibold mb-2">Post not found</h1>
        <Link to="/blog" className="text-[#064E3B] hover:underline">Back to blog →</Link>
      </div>
    );
  }

  const paragraphs = post.body.split("\n\n").filter(Boolean);

  return (
    <article className="min-h-screen">
      <SEO
        title={`${post.title} | IV Therapy Finder`}
        description={post.excerpt}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          datePublished: post.published_at,
          author: { "@type": "Person", name: post.author },
          publisher: { "@type": "Organization", name: "IV Therapy Finder" },
          image: post.hero_image,
        }}
      />

      <section className="bg-gradient-to-b from-[#F3F4F1] to-[#FAFAFA] border-b border-stone-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/blog" className="text-xs text-stone-500 hover:text-[#064E3B] inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> All articles</Link>
          <Badge variant="secondary" className="bg-[#064E3B]/8 text-[#064E3B] hover:bg-[#064E3B]/10 border-0 mb-3">{post.category}</Badge>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-stone-900 leading-[1.1] mb-4">{post.title}</h1>
          <p className="text-lg text-stone-600 mb-5">{post.excerpt}</p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500">
            <span>By <strong className="text-stone-700">{post.author}</strong></span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(post.published_at).toLocaleDateString()}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.reading_minutes} min read</span>
          </div>
        </div>
      </section>

      {post.hero_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2">
          <img src={post.hero_image} alt={post.title} className="w-full rounded-2xl aspect-[21/9] object-cover" />
        </div>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* If body is HTML (from TipTap), render as HTML. Otherwise fall back to the legacy mini-markdown parser. */}
        {/<[a-z][\s\S]*>/i.test(post.body) ? (
          <div
            className="prose-content tiptap-body text-stone-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: post.body }}
          />
        ) : (
          <div className="prose-content space-y-5 text-stone-800 leading-relaxed">
            {paragraphs.map((p, i) => {
              if (p.startsWith("**") && p.endsWith("**")) {
                return <h3 key={i} className="font-display text-xl font-semibold text-stone-900 mt-8">{p.replace(/^\*\*|\*\*$/g, "")}</h3>;
              }
              const parts = p.split(/(\*\*[^*]+\*\*)/g);
              return (
                <p key={i} className="text-base">
                  {parts.map((part, j) =>
                    part.startsWith("**") && part.endsWith("**")
                      ? <strong key={j} className="text-stone-900">{part.slice(2, -2)}</strong>
                      : <span key={j}>{part}</span>
                  )}
                </p>
              );
            })}
          </div>
        )}

        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-[#064E3B] to-[#0a6b54] text-white">
          <div className="text-xs uppercase tracking-wider text-white/70 mb-2">Ready to find a provider?</div>
          <h3 className="font-display text-xl font-semibold mb-2">Browse verified IV therapy providers near you</h3>
          <p className="text-sm text-white/85 mb-4">Real patient reviews. Verified credentials. Direct booking.</p>
          <Link to="/search" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-white text-[#064E3B] text-sm font-semibold hover:bg-stone-100 transition">Find providers →</Link>
        </div>
      </section>
    </article>
  );
}
